import _extends from '@babel/runtime/helpers/esm/extends';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime/helpers/esm/objectWithoutProperties';
import React, { useState, useCallback, useContext, useMemo } from 'react';
import clsx from 'clsx';
import { CombinedInput } from '@swift-paypal/pp-react-combined-input';
import { IntlContext } from '@swift-paypal/pp-react-intl';
import { Flag } from '@swift-paypal/pp-react-flag';
import { MaskedTextInput } from '@swift-paypal/pp-react-masked-text-input';
import { DropdownMenu } from '@swift-paypal/pp-react-dropdown-menu';
import '../__generated__/styles/phone-input.css';
import { useTheme } from '@swift-paypal/pp-react-theme';

// 55 fixed line Countries with variable length
// 38 mobile line Countries with variable length
// 21 of those instersect
var VAR_LENGTH_COUNTRIES_FIXED_LINE = ['AT', 'AX', 'BD', 'BG', 'CD', 'CN', 'CU', 'CW', 'DE', 'DZ', 'EG', 'FI', 'GB', 'HR', 'ID', 'IE', 'IL', 'IQ', 'IR', 'IT', 'KE', 'KH', 'KI', 'KP', 'KR', 'LB', 'LR', 'LU', 'MM', 'MN', 'MU', 'MW', 'MY', 'NA', 'NG', 'PG', 'PH', 'PK', 'PL', 'PY', 'RO', 'RS', 'RW', 'SE', 'SH', 'SK', 'SO', 'SR', 'SY', 'TK', 'TW', 'VA', 'WS', 'YE', 'ZW'];
var VAR_LENGTH_COUNTRIES_MOBILE = ['AD', 'AR', 'AT', 'AX', 'BA', 'BG', 'BR', 'CD', 'CW', 'DE', 'EE', 'FI', 'GA', 'HR', 'ID', 'IS', 'IT', 'KH', 'KR', 'LB', 'LI', 'LR', 'MC', 'MM', 'MX', 'MY', 'NZ', 'PA', 'PN', 'RS', 'SB', 'SO', 'TK', 'TV', 'VA', 'WS', 'XK', 'ZA'];
// Get phone format from IntlProvider
function getPhoneFormat(intlContext, phoneRegion, formatType, isMobile) {
  var providedCountryCode = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : null;
  var providedPhoneFormat = arguments.length > 5 && arguments[5] !== undefined ? arguments[5] : '';

  if (providedCountryCode && providedPhoneFormat) {
    return {
      countryCode: providedCountryCode,
      pattern: providedPhoneFormat
    };
  } else if (phoneRegion && intlContext && intlContext.phoneFormatter) {
    var phoneType = isMobile ? 'MOBILE' : 'FIXED_LINE';
    var phoneNumberFormat = intlContext.phoneFormatter({
      phoneRegion: phoneRegion,
      style: formatType
    });
    var phoneExampleGenerator = intlContext.exampleGenerator({
      feature: 'phone',
      region: phoneRegion,
      type: phoneType
    });
    var exampleNumberObj = phoneExampleGenerator.generate();
    var countryCode = exampleNumberObj.countryCode,
        nationalNumber = exampleNumberObj.nationalNumber;
    var formattedNumber = phoneNumberFormat.format({
      countryCode: countryCode,
      nationalNumber: nationalNumber
    });
    var phoneNumber = formattedNumber;

    if (formattedNumber.includes('+')) {
      phoneNumber = formattedNumber.split("+".concat(countryCode))[1].trim();
    }

    var pattern = phoneNumber.replace(/\d/g, '0');
    var varLengthCountries = isMobile ? VAR_LENGTH_COUNTRIES_MOBILE : VAR_LENGTH_COUNTRIES_FIXED_LINE;

    if (varLengthCountries.includes(phoneRegion)) {
      var patternLength = pattern.match(/0/g).length;
      var optionalDigits = '0'.repeat(20 - patternLength);
      pattern = pattern + "[".concat(optionalDigits, "]");
    }

    return {
      countryCode: countryCode,
      pattern: pattern
    };
  }

  return {
    countryCode: providedCountryCode || '',
    pattern: providedPhoneFormat || '00000000000000000000'
  };
}

var styles = {"ppvx_phone-input":"ppvx_phone-input___1-4-0","ppvx_combined-input":"ppvx_combined-input___1-4-0","ppvx_phone-input__combined-input":"ppvx_phone-input__combined-input___1-4-0","ppvx_phone-input__input-with-menu":"ppvx_phone-input__input-with-menu___1-4-0","ppvx_phone-input__input__control":"ppvx_phone-input__input__control___1-4-0","ppvx_text-input__control":"ppvx_text-input__control___1-4-0","ppvx_phone-input__menu":"ppvx_phone-input__menu___1-4-0","ppvx_phone-input__menu__btn-thumbnail":"ppvx_phone-input__menu__btn-thumbnail___1-4-0","ppvx_phone-input__menu__list":"ppvx_phone-input__menu__list___1-4-0","ppvx_phone-input__menu__button":"ppvx_phone-input__menu__button___1-4-0","ppvx_text-input__prefix":"ppvx_text-input__prefix___1-4-0","ppvx_text-input__prefix-wrapper":"ppvx_text-input__prefix-wrapper___1-4-0","ppvx_text-input__control--prefix":"ppvx_text-input__control--prefix___1-4-0","ppvx--v2":"ppvx--v2___1-4-0","ppvx--v1":"ppvx--v1___1-4-0","ppvx_phone-input__menu__button__affordance":"ppvx_phone-input__menu__button__affordance___1-4-0","ppvx_phone-input__menu__button__contents":"ppvx_phone-input__menu__button__contents___1-4-0"};

var PhoneCodeDropdown = function (_ref) {
  var selectedItemIndex = _ref.selectedItemIndex,
      rtl = _ref.rtl,
      countryOptions = _ref.countryOptions,
      options = _ref.options,
      flagId = _ref.flagId,
      codeId = _ref.codeId,
      theme = _ref.theme,
      rest = _objectWithoutProperties(_ref, ["selectedItemIndex", "rtl", "countryOptions", "options", "flagId", "codeId", "theme"]);

  var isV2 = theme === 'v2';
  return /*#__PURE__*/React.createElement(DropdownMenu, _extends({
    renderButtonContent: function renderButtonContent() {
      var selectedOption = countryOptions[selectedItemIndex];
      var countryPhoneCode = '';

      if (selectedOption && selectedOption.countryPhoneCode) {
        //istanbul ignore next
        if (rtl) {
          countryPhoneCode = "".concat(selectedOption.countryPhoneCode, "+");
        } else {
          countryPhoneCode = "+".concat(selectedOption.countryPhoneCode);
        }
      }

      if (selectedOption && selectedOption.phoneRegion) {
        return /*#__PURE__*/React.createElement("span", {
          className: clsx(isV2 && styles['ppvx_phone-input__menu__button__contents'])
        }, /*#__PURE__*/React.createElement("span", {
          className: styles['ppvx_phone-input__menu__btn-thumbnail']
        }, /*#__PURE__*/React.createElement(Flag, {
          id: flagId,
          countryCode: selectedOption.phoneRegion,
          size: isV2 ? 'md' : 'xs',
          "aria-label": selectedOption.countryName,
          theme: theme
        })), /*#__PURE__*/React.createElement("span", {
          id: codeId
        }, countryPhoneCode));
      }

      return /*#__PURE__*/React.createElement(React.Fragment, null, countryPhoneCode);
    },
    options: options,
    selectedItemIndex: selectedItemIndex,
    ariaLabelledby: "".concat(flagId, " ").concat(codeId),
    theme: theme
  }, rest));
};

var PhoneInputWithMenu = function (_ref) {
  var name = _ref.name,
      countryOptions = _ref.countryOptions,
      _ref$inputLabel = _ref.inputLabel,
      inputLabel = _ref$inputLabel === void 0 ? 'Phone Number' : _ref$inputLabel,
      _ref$menuLabel = _ref.menuLabel,
      menuLabel = _ref$menuLabel === void 0 ? 'Code' : _ref$menuLabel,
      selectedIndex = _ref.selectedIndex,
      className = _ref.className,
      inputClassName = _ref.inputClassName,
      menuClassName = _ref.menuClassName,
      _ref$mobile = _ref.mobile,
      mobile = _ref$mobile === void 0 ? false : _ref$mobile,
      onSelection = _ref.onSelection,
      theme = _ref.theme,
      rest = _objectWithoutProperties(_ref, ["name", "countryOptions", "inputLabel", "menuLabel", "selectedIndex", "className", "inputClassName", "menuClassName", "mobile", "onSelection", "theme"]);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isRtl = _useState2[0],
      setRtl = _useState2[1];

  var _theme = useTheme('PhoneInputWithMenu', theme);

  var containerRef = useCallback(function (node) {
    if (node !== null && window && window.getComputedStyle(node)) {
      var computedStyle = window.getComputedStyle(node);
      setRtl(computedStyle && computedStyle.direction === 'rtl');
    }
  }, []);
  var wrapperClasses = clsx(styles['ppvx_phone-input'], _defineProperty({}, styles['ppvx--v2'], _theme && _theme === 'v2'), className);
  var optionText;
  var optionCode;
  var optionPattern;
  var optionCountryRegion;
  var optionCountryName;
  var menuOptions;
  var preselectedMask = '00000000000000000000';
  var intlContext = useContext(IntlContext);

  var constructOptionProps = function (countryName, countryPhoneCode, countryRegion, rtl, menuTheme) {
    /* istanbul ignore next */
    optionText = rtl ? "".concat(countryName, " ").concat(countryPhoneCode, "+") : "".concat(countryName, " +").concat(countryPhoneCode);
    return {
      primaryText: optionText,
      decoration: /*#__PURE__*/React.createElement(Flag, {
        countryCode: countryRegion,
        size: "md",
        theme: menuTheme
      })
    };
  };

  menuOptions = useMemo(function () {
    return countryOptions.map(function (country, i) {
      var phoneParts = getPhoneFormat(intlContext, country.phoneRegion, 'international', mobile, country.countryPhoneCode, country.phoneFormat);
      /* istanbul ignore next */

      var displayNamesObj = intlContext && intlContext.getDisplayNamesObject && intlContext.getDisplayNamesObject({
        type: 'region'
      });
      var countryCode = phoneParts.countryCode,
          pattern = phoneParts.pattern;
      optionCountryRegion = country.phoneRegion; //this one must be provided

      optionCode = country.countryPhoneCode || countryCode; //if provided use that

      optionPattern = country.phoneFormat || pattern; //if provided use that

      /* istanbul ignore next */

      optionCountryName = country.countryName || displayNamesObj && displayNamesObj.select(country.phoneRegion); //if provided use that

      countryOptions[i].countryPhoneCode = optionCode;
      countryOptions[i].phoneFormat = optionPattern;
      countryOptions[i].countryName = optionCountryName;

      if (selectedIndex === i) {
        preselectedMask = optionPattern;
      }

      return constructOptionProps(optionCountryName, optionCode, optionCountryRegion, isRtl, _theme);
    });
  }, [countryOptions]);

  var _useState3 = useState(preselectedMask),
      _useState4 = _slicedToArray(_useState3, 2),
      currentMask = _useState4[0],
      setMask = _useState4[1];

  var _useState5 = useState(false),
      _useState6 = _slicedToArray(_useState5, 2),
      hasFocus = _useState6[0],
      setFocus = _useState6[1];

  var menuClasses = clsx(styles['ppvx_phone-input__menu'], menuClassName);
  var inputClasses = clsx(styles['ppvx_phone-input__input-with-menu'], inputClassName);
  var generatedFlagId = "phone-input-country-flag_".concat(Math.random().toFixed(8).toString().replace('0.', ''));
  var generatedCodeId = "phone-input-country-code_".concat(Math.random().toFixed(8).toString().replace('0.', ''));
  var dropdownEl = /*#__PURE__*/React.createElement(PhoneCodeDropdown, {
    label: menuLabel,
    options: menuOptions,
    countryOptions: countryOptions,
    selectedItemIndex: selectedIndex,
    onSelection: function handleSelection(idx) {
      setMask(countryOptions[idx].phoneFormat);
      onSelection(idx);
      setFocus(true);
    },
    className: menuClasses,
    menuListClassName: styles['ppvx_phone-input__menu__list'],
    menuButtonClassName: styles['ppvx_phone-input__menu__button'],
    menuLabelClassName: styles['ppvx_phone-input__menu__label'],
    sheetClassName: styles['ppvx_phone-input__menu__list'],
    rtl: isRtl,
    skipButtonFocus: true,
    flagId: generatedFlagId,
    codeId: generatedCodeId,
    theme: _theme
  });
  var inputEl = /*#__PURE__*/React.createElement(MaskedTextInput, _extends({
    type: "tel",
    name: name,
    label: inputLabel,
    maskOptions: {
      mask: currentMask
    },
    className: inputClasses,
    inputClassName: styles['ppvx_phone-input__input__control'],
    setFocus: hasFocus,
    onBlur: function onBlur() {
      return setFocus(false);
    },
    "aria-describedby": "".concat(generatedFlagId, " ").concat(generatedCodeId)
  }, rest));
  return /*#__PURE__*/React.createElement("div", {
    ref: containerRef,
    className: wrapperClasses
  }, /*#__PURE__*/React.createElement(CombinedInput, {
    theme: _theme,
    className: styles['ppvx_phone-input__combined-input']
  }, dropdownEl, inputEl));
};

var PhoneInput = function (_ref) {
  var name = _ref.name,
      _ref$label = _ref.label,
      label = _ref$label === void 0 ? 'Phone Number' : _ref$label,
      countryOptions = _ref.countryOptions,
      className = _ref.className,
      inputClassName = _ref.inputClassName,
      _ref$formatType = _ref.formatType,
      formatType = _ref$formatType === void 0 ? countryOptions.countryPhoneCode ? 'international' : 'national' : _ref$formatType,
      _ref$mobile = _ref.mobile,
      mobile = _ref$mobile === void 0 ? false : _ref$mobile,
      theme = _ref.theme,
      rest = _objectWithoutProperties(_ref, ["name", "label", "countryOptions", "className", "inputClassName", "formatType", "mobile", "theme"]);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      isRtl = _useState2[0],
      setRtl = _useState2[1];

  var _theme = useTheme('PhoneInput', theme);

  var containerRef = useCallback(function (node) {
    if (node !== null && window && window.getComputedStyle(node)) {
      var computedStyle = window.getComputedStyle(node);
      setRtl(computedStyle && computedStyle.direction === 'rtl');
    }
  }, []);
  var phoneMask = '';
  var codePrefix = '';
  var intlContext = useContext(IntlContext);
  var phoneParts = useMemo(function () {
    return getPhoneFormat(intlContext, countryOptions.phoneRegion, formatType, mobile, countryOptions.countryPhoneCode, countryOptions.phoneFormat);
  }, [countryOptions.phoneRegion, formatType, mobile]);
  var countryCode = phoneParts.countryCode,
      pattern = phoneParts.pattern;
  phoneMask = pattern;
  codePrefix = formatType === 'international' ? countryCode : '';
  var wrapperClasses = clsx(styles['ppvx_phone-input'], _defineProperty({}, styles['ppvx--v2'], _theme && _theme === 'v2'), className);
  var codeSuffix = codePrefix;

  if (formatType === 'international') {
    //istanbul ignore next
    codePrefix = isRtl ? '' : "+".concat(codePrefix); //istanbul ignore next

    codeSuffix = isRtl ? "".concat(codeSuffix, "+") : '';
  }

  return /*#__PURE__*/React.createElement("div", {
    ref: containerRef,
    className: wrapperClasses
  }, /*#__PURE__*/React.createElement(MaskedTextInput, _extends({
    type: "tel",
    name: "phoneInput-".concat(name),
    label: label,
    prefix: codePrefix,
    suffix: codeSuffix,
    maskOptions: {
      mask: phoneMask
    },
    className: inputClassName,
    inputClassName: styles['ppvx_phone-input__input__control'],
    theme: _theme
  }, rest)));
};

export { PhoneInput, PhoneInputWithMenu };
